import React, { useState, useRef } from 'react';

/**
 * Side Navigation Bar component
 */
const sidebarItems = [
  { id: 'profile', icon: '👤', label: 'My Profile' },
  { id: 'trips', icon: '🚗', label: 'My Trips' },
  { id: 'orders', icon: '📦', label: 'My Orders' },
  { id: 'tripPlanner', icon: '🗺️', label: 'Trip Planner' },
  { id: 'payments', icon: '💳', label: 'Payments' },
  { id: 'support', icon: '💬', label: 'Support' },
  { id: 'emergency', icon: '🚨', label: 'Emergency' },
];

const bottomItems = [
  { id: 'settings', icon: '⚙️', label: 'Settings' },
];

export function Sidebar({ isOpen, onClose, onNavigate }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const sidebarRef = useRef(null);
  
  // Minimum swipe distance for close gesture
  const minSwipeDistance = 50;
  
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    
    if (isLeftSwipe && onClose) {
      onClose();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleItemClick = (itemId) => {
    if (onNavigate) {
      onNavigate(itemId);
    }
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        data-testid="sidebar-backdrop"
      />
      
      {/* Sidebar Panel */}
      <div
        ref={sidebarRef}
        className="absolute left-0 top-0 bottom-0 w-64 flex flex-col overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid="sidebar-panel"
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-4">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8 mt-4">
            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
              <span className="text-2xl">🍽️</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">HiWAY</h2>
              <p className="text-white/70 text-sm">Highway Food</p>
            </div>
          </div>
          
          {/* Menu Items */}
          <nav className="flex-1 space-y-1">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className="w-full flex items-center gap-3 px-3 py-3 text-white hover:bg-white/20 rounded-lg transition-colors"
                data-testid={`sidebar-item-${item.id}`}
              >
                <span className="text-xl">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
          
          {/* Bottom Items */}
          <div className="border-t border-white/20 pt-4 mt-4">
            {bottomItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className="w-full flex items-center gap-3 px-3 py-3 text-white hover:bg-white/20 rounded-lg transition-colors"
                data-testid={`sidebar-item-${item.id}`}
              >
                <span className="text-xl">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
